import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import {
	adminAuthControllerChangeFirstPassword,
	adminAuthControllerChangePassword,
} from '@api/sweetnya/admin-auth/admin-auth';
import { useAuthStore } from '@store/useAuthStore';
import { isNumericSpecialChars, isRepeatedChars, isRequiredChars, isRequiredLength } from '@shared/utils/common';
import theme from '@/styles/theme';
import ModalContainer, { ButtonGroup } from '../modal/ModalContainer';
import StyledButton from '../atoms/StyledButton';
import StyledPassword from '@/shared/components/atoms/StyledPassword';

export type PasswordChangeMode = 'normal' | 'expired' | 'first' | 'temp';

type PasswordChangeModalProps = {
	mode?: PasswordChangeMode;
	isInit?: boolean;
	required?: boolean;
	onClose?: () => void;
	setPasswordModal?: (open: boolean) => void;
};

const getModalTitle = (mode: PasswordChangeMode) => {
	if (mode === 'expired') return '비밀번호 변경 (180일 경과)';
	if (mode === 'first' || mode === 'temp') return '비밀번호 변경 (최초 로그인)';
	return '비밀번호 변경';
};

const PasswordChangeModal = ({
	mode,
	isInit,
	required = false,
	onClose,
	setPasswordModal,
}: PasswordChangeModalProps) => {
	const { logout } = useAuthStore();
	const resolvedMode: PasswordChangeMode = mode ?? (isInit ? 'first' : 'normal');
	const isFirstPassword = resolvedMode === 'first' || resolvedMode === 'temp';
	const [submitting, setSubmitting] = useState(false);
	const [inputs, setInputs] = useState({
		currentPassword: '',
		newPassword1: '',
		newPassword2: '',
	});

	const { isEightLength, hasRequiredChars, hasNoRepeatedChars, isSamePassword } = useMemo(() => {
		const { newPassword1, newPassword2 } = inputs;

		if (newPassword1.length === 0) {
			return {
				isEightLength: null,
				hasRequiredChars: null,
				hasNoRepeatedChars: null,
				isSamePassword: null,
			};
		}

		return {
			isEightLength: isRequiredLength(newPassword1),
			hasRequiredChars: isRequiredChars(newPassword1) || isNumericSpecialChars(newPassword1),
			hasNoRepeatedChars: !isRepeatedChars(newPassword1),
			isSamePassword: newPassword1 === newPassword2,
		};
	}, [inputs]);

	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setInputs((prev) => ({ ...prev, [name]: value }));
	}, []);

	const handleClose = useCallback(() => {
		if (required) return;
		if (onClose) onClose();
		if (setPasswordModal) setPasswordModal(false);
	}, [onClose, required, setPasswordModal]);

	const handleSave = useCallback(async () => {
		if (submitting) return;

		if (!isFirstPassword && !inputs.currentPassword) {
			toast.error('현재 비밀번호를 입력해주세요.');
			return;
		}

		if (!isEightLength || !hasRequiredChars || !hasNoRepeatedChars || !isSamePassword) {
			toast.error('비밀번호 유효성을 만족하지 않습니다.');
			return;
		}

		try {
			setSubmitting(true);
			const payload = {
				newPassword: inputs.newPassword1,
				newPasswordConfirm: inputs.newPassword2,
			};
			const res = isFirstPassword
				? await adminAuthControllerChangeFirstPassword(payload)
				: await adminAuthControllerChangePassword({
					...payload,
					currentPassword: inputs.currentPassword,
				});

			alert(`${res.message || '비밀번호가 성공적으로 변경되었습니다.'}\n다시 로그인해주세요.`);
			logout();
		} catch {
			// 에러 토스트는 axios 응답 인터셉터에서 처리
		} finally {
			setSubmitting(false);
		}
	}, [
		hasNoRepeatedChars,
		hasRequiredChars,
		inputs,
		isEightLength,
		isFirstPassword,
		isSamePassword,
		logout,
		submitting,
	]);

	return (
		<ModalContainer
			isFixed={required}
			isLoading={submitting}
			maxWidth={508}
			title={getModalTitle(resolvedMode)}
			onClose={handleClose}>
			<Form>
				{!isFirstPassword && (
					<>
						<Title>현재 비밀번호</Title>
						<StyledPassword
							margin="0 0 -16px"
							name="currentPassword"
							value={inputs.currentPassword}
							onChange={onChange}
							placeholder="현재 비밀번호"
						/>
					</>
				)}
				<Title>새로운 비밀번호</Title>
				<StyledPassword
					name="newPassword1"
					value={inputs.newPassword1}
					onChange={onChange}
					placeholder="새 비밀번호"
				/>
				<StyledPassword
					margin="12px 0"
					name="newPassword2"
					value={inputs.newPassword2}
					onChange={onChange}
					onKeyPress={(e) => e.key === 'Enter' && handleSave()}
					placeholder="새 비밀번호 확인"
				/>
			</Form>
			<ValidateBox>
				<ValidateText $active={isEightLength}>- 비밀번호 8자리 이상</ValidateText>
				<ValidateText $active={hasRequiredChars}>- 대문자, 소문자, 숫자, 특수문자 포함</ValidateText>
				<ValidateText $active={hasNoRepeatedChars}>- 반복되는 문자 사용 금지</ValidateText>
				<ValidateText $active={isSamePassword}>- 비밀번호 확인</ValidateText>
			</ValidateBox>
			<ButtonGroup>
				{!required && (
					<StyledButton
						width={140}
						title="취소"
						fontColor={theme.colors.blackColor}
						margin="0 8px 0 0"
						border={`1px solid ${theme.colors.lightGrayBorder}`}
						bgColor={theme.colors.whiteColor}
						onClick={handleClose}
					/>
				)}
				<StyledButton
					title={submitting ? '변경 중' : '변경하기'}
					width={140}
					fontColor={theme.colors.whiteColor}
					border="none"
					bgColor={theme.colors.blackColor}
					onClick={handleSave}
					disabled={submitting}
				/>
			</ButtonGroup>
		</ModalContainer>
	);
};

export default PasswordChangeModal;

const Form = styled.form``;
const Title = styled.div`
  margin: 36px 0 12px;
`;
const ValidateBox = styled.div``;
const ValidateText = styled.div<{ $active: boolean | null; }>`
  font-size: 14px;
  color: ${theme.colors.lightGrayFontColor};
  margin-bottom: 6px;

  ${({ $active }) => $active === true && css`
    color: ${theme.colors.activeSkyBlue};
  `};
  ${({ $active }) => $active === false && css`
    color: ${theme.colors.activeOrange};
  `};
`;
